import { useCallback, useState } from 'react';

/**
 * useState-like hook whose value is persisted to localStorage under `key`.
 * - Reads the persisted value on first render (falling back to `defaultValue` when absent,
 *   unparseable, or when storage is unavailable).
 * - Writes on every update; write failures are swallowed (the in-memory value still updates).
 * NOTE: this hook does not validate the shape of the stored value — a consumer with a
 * constrained type (e.g. a string union) must coerce/guard the value it reads back.
 */
export function useLocalStorageState<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? defaultValue : (JSON.parse(raw) as T);
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage unavailable — keep the in-memory value only */
      }
    },
    [key],
  );

  return [value, set];
}
